import * as React from 'react';
import { FormGroup, FormSelect, FormSelectOption, Label } from '@patternfly/react-core';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { buildDaily, netInWindow, rebucket, sliceWindow, verdictOf } from '../../../../shared/riskregister/trend';
import type { Payload, Row, TrendWindow, ViewState } from '../../../../shared/riskregister/types';

const TREND_COLORS = {
  opened: 'var(--accent-blue)',
  resolved: 'var(--accent-green)',
  open: 'var(--accent-purple)',
} as const;

const WINDOW_OPTIONS: Array<{ value: TrendWindow; label: string }> = [
  { value: '30d', label: 'Last 30 days' },
  { value: '60d', label: 'Last 60 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: 'all', label: 'All time' },
];

function verdictLabel(net: number): { color: 'green' | 'red' | 'blue'; text: string } {
  const verdict = verdictOf(net);
  if (verdict === 'up') return { color: 'red', text: 'Risk backlog growing' };
  if (verdict === 'down') return { color: 'green', text: 'Risk backlog shrinking' };
  return { color: 'blue', text: 'Risk backlog stable' };
}

export const TrendPanel: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
  state: ViewState;
  onChange: (change: Partial<ViewState>) => void;
}> = ({ payload, rows, state, onChange }) => {
  const daily = React.useMemo(() => buildDaily(rows, payload.as_of), [payload.as_of, rows]);
  const windowed = React.useMemo(() => sliceWindow(daily, state.win, payload.as_of), [daily, payload.as_of, state.win]);
  const chartData = React.useMemo(() => rebucket(windowed, state.win), [state.win, windowed]);
  const net = React.useMemo(() => netInWindow(rows, state.win, payload.as_of), [payload.as_of, rows, state.win]);
  const verdict = verdictLabel(net.net);
  const scope = state.area === 'ALL' ? 'all platform areas' : state.area;

  return (
    <>
      <FormGroup label="Trend window" fieldId="risk-register-trend-window">
        <FormSelect
          id="risk-register-trend-window"
          value={state.win}
          onChange={(_event, win) => onChange({ win: win as TrendWindow })}
        >
          {WINDOW_OPTIONS.map((option) => (
            <FormSelectOption key={option.value} value={option.value} label={option.label} />
          ))}
        </FormSelect>
      </FormGroup>

      <p>
        <Label color={verdict.color} isCompact>
          {verdict.text}
        </Label>{' '}
        {net.opened} opened and {net.resolved} resolved: net {net.net > 0 ? '+' : ''}
        {net.net}.
      </p>
      <p>
        Scope: {rows.length} item{rows.length === 1 ? '' : 's'} in {scope}. Area, accountability drill-down, and search
        apply here; scope, stage, due, idle, score, ownership, category, priority, queue, and due-history filters
        intentionally do not.
      </p>

      {chartData.length ? (
        <div
          className="risk-register__chart"
          role="img"
          aria-label={`Opened, resolved, and cumulative open risk for ${WINDOW_OPTIONS.find((option) => option.value === state.win)?.label}`}
        >
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 16, right: 24, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
                minTickGap={28}
              />
              <YAxis
                yAxisId="events"
                allowDecimals={false}
                tick={{ fill: 'var(--text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
              />
              <YAxis
                yAxisId="open"
                orientation="right"
                allowDecimals={false}
                tick={{ fill: 'var(--text-secondary)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--border)' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                }}
              />
              <Legend />
              <Bar yAxisId="events" dataKey="opened" name="Opened" fill={TREND_COLORS.opened} />
              <Bar yAxisId="events" dataKey="resolved" name="Resolved" fill={TREND_COLORS.resolved} />
              <Line
                yAxisId="open"
                type="monotone"
                dataKey="cum"
                name="Cumulative open"
                stroke={TREND_COLORS.open}
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="risk-register__empty">No trend history is available for this selection.</p>
      )}

      <ul>
        <li>Opened is dated from Jira creation and resolved is dated from Jira resolution.</li>
        <li>The cumulative-open line retains the full-history cumulative value at the start of this window.</li>
        <li>Resolution is an outcome count, not evidence that a change was deployed.</li>
        <li>
          Historical trend intentionally ignores state filters so changing a current-state filter cannot rewrite
          history.
        </li>
      </ul>
    </>
  );
};
